// @ts-nocheck
import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { useIsDemo } from "@/hooks/common/useIsDemo"
import { notifyFeature } from "@/utils/demoNotifications"
import { Search, Plus, Folder, DollarSign, LayoutGrid, Table2 } from "lucide-react"
import { AriIcon } from "@/components/sidebar/icons"
import { Badge } from "@/components/ui/badge"
import { StatBlock } from "@/components/ui"
import { Button } from "@/components/ui/Button"
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
} from "@/components/ui/alert-dialog"
import { Tabs, TabsList, TabsTab, TabsPanel } from "@/components/ui/tabs"
import { Input } from "@/components/ui/Input"
import { PATH_ROOT } from "@/routes/paths"
import { DealPipelineBoard } from "./components/DealPipelineBoard"
import { PipelinePresets } from "./components/PipelinePresets"
import { AriPipelineToolbar, AriSuggestedDealCard } from "./components/AriDealKanbanCard"
import { DealsTableView } from "./DealsTableView"
import { ARI_PIPELINE_PRESETS, PIPELINE_PRESETS } from "@/constants/deal.constants"
import { useDealPipelineData } from "@/hooks/deals/useDealPipelineData"
import { formatCurrency } from "@/views/deals/utils"
import type { Deal, PipelineStage } from "@/types/deal.types"

type ViewMode = "pipeline" | "table"

const DealPipelinePage = () => {
  const navigate = useNavigate()
  const isDemo = useIsDemo()
  const { initialDeals, suggestedDeals, isAriPipeline, deleteDeal } = useDealPipelineData()
  const [deals, setDeals] = useState<Deal[]>(initialDeals)
  const [showSuggested, setShowSuggested] = useState(false)

  // Page-level delete confirmation. The kanban card's "Delete" menu item
  // signals intent via onRequestDelete; the AlertDialog + the actual
  // deleteDeal mutation live here so confirmation + deletion share one
  // place (the pattern DealKanbanCard:46-51 describes). deleteDeal comes
  // from useDealPipelineData's useDealOperations instance, so the SWR
  // refetch lands on the same cache key the kanban renders from.
  const [pendingDeleteDeal, setPendingDeleteDeal] = useState<Deal | null>(null)
  const [deletingDeal, setDeletingDeal] = useState(false)

  const confirmDeleteDeal = async () => {
    if (!pendingDeleteDeal) return
    const target = pendingDeleteDeal
    // Demo mode is route-authoritative (useIsDemo). Demo deals have no
    // backend row, so NEVER call the real /deals/delete — surface the
    // Coming-Soon toast and leave the board untouched. (A local removal
    // would flicker back on the next initialDeals recompute since the
    // demo dataset is static, so a true no-op is the honest behavior.)
    if (isDemo) {
      setPendingDeleteDeal(null)
      notifyFeature("Delete deal", true)
      return
    }
    setDeletingDeal(true)
    try {
      await deleteDeal(target.id)
      // Mirror the optimistic removal from useDealOperations into the
      // page's local `deals` state so the card disappears immediately;
      // the SWR refetch inside deleteDeal reconciles the canonical list.
      setDeals((prev) => prev.filter((d) => d.id !== target.id))
      toast.success("Deal deleted")
      setPendingDeleteDeal(null)
    } catch (err) {
      console.error("Failed to delete deal:", err)
      toast.error("Could not delete deal")
    } finally {
      setDeletingDeal(false)
    }
  }

  useEffect(() => {
    setDeals(initialDeals)
  }, [initialDeals])

  const [searchQuery, setSearchQuery] = useState("")
  const [activePreset, setActivePreset] = useState(isAriPipeline ? "ari-default" : "default")
  const [viewMode, setViewMode] = useState<ViewMode>("pipeline")

  const presets = isAriPipeline ? ARI_PIPELINE_PRESETS : PIPELINE_PRESETS

  const visibleColumns = useMemo(() => {
    const preset = presets.find((p) => p.id === activePreset)
    return preset?.columns ?? (isAriPipeline ? ARI_PIPELINE_PRESETS[0].columns : PIPELINE_PRESETS[0].columns)
  }, [activePreset, isAriPipeline, presets])

  const filteredDeals = useMemo(() => {
    return deals.filter((deal) => {
      const q = searchQuery.toLowerCase()
      const matchesSearch =
        deal.name.toLowerCase().includes(q) ||
        deal.type.toLowerCase().includes(q) ||
        deal.description.toLowerCase().includes(q) ||
        (deal.contactName?.toLowerCase().includes(q) ?? false) ||
        (deal.contactCompany?.toLowerCase().includes(q) ?? false)
      return matchesSearch
    })
  }, [deals, searchQuery])

  const stats = useMemo(() => {
    const total = deals.length
    const inProgress = deals.filter((d) => d.stage === "in_progress").length
    const signing = deals.filter((d) => d.stage === "signing").length
    const complete = deals.filter((d) => d.stage === "complete" || d.stage === "won" || d.stage === "closed").length
    const totalValue = deals.reduce((sum, d) => sum + d.value, 0)
    return { total, inProgress, signing, complete, totalValue }
  }, [deals])

  const addSuggestedDeal = (deal: Deal) => {
    setDeals((prev) => {
      if (prev.some((d) => d.id === deal.id)) return prev
      return [...prev, { ...deal, stage: "lead" as const }]
    })
  }

  return (
    <Tabs
      value={viewMode}
      onValueChange={(v) => setViewMode(v as ViewMode)}
      className="flex w-full flex-col bg-background"
    >
      <div className="sticky top-0 z-10 bg-background">
        {isAriPipeline ? (
          <>
            <section className="shrink-0 border-b border-border">
              <div className="flex items-start justify-between gap-4 px-5 py-4">
                <div className="flex flex-col gap-1">
                  <h1 className="text-xl font-semibold">Deals</h1>
                  <p className="text-sm text-muted-foreground">
                    Track negotiations across your pipeline.
                  </p>
                </div>
                <div className="flex flex-wrap gap-6">
                  <StatBlock
                    label="Active deals"
                    value={deals.length}
                    align="left"
                    valueClassName="text-2xl"
                  />
                  <StatBlock
                    label="In progress"
                    value={stats.inProgress}
                    align="left"
                    valueClassName="text-2xl"
                  />
                  <StatBlock
                    label="Closed"
                    value={stats.complete}
                    align="left"
                    valueClassName="text-2xl"
                  />
                </div>
              </div>
            </section>
            <header className="shrink-0 border-b border-border bg-card/40 px-5 py-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" size="sm" className="text-muted-foreground">
                  All deals
                </Badge>
                <Badge variant="default" size="sm">
                  {deals.length}
                </Badge>
                <div className="ml-auto flex flex-wrap items-center gap-2">
                  {/* Suggested Deals is a demo-only affordance (curated mock
                      deals from the inbox). On the real route suggestedDeals is
                      empty, so the toggle would read "0 Suggested Deals" — hide
                      it entirely when there's nothing to suggest. */}
                  {suggestedDeals.length > 0 && (
                    <Button
                      variant="secondary"
                      size="sm"
                      rounded="full"
                      className="gap-1.5"
                      onClick={() => setShowSuggested((v) => !v)}
                      aria-pressed={showSuggested}
                    >
                      <AriIcon variant="gradient" className="size-3.5" aria-hidden="true" />
                      {suggestedDeals.length} Suggested Deals
                    </Button>
                  )}
                  <Button
                    size="sm"
                    className="gap-1.5"
                    onClick={() => navigate(isDemo ? PATH_ROOT.demo.newDeal : PATH_ROOT.main.newDeal)}
                  >
                    <Plus className="size-3.5" aria-hidden="true" />
                    Add Deal
                  </Button>
                </div>
              </div>
            </header>
            <AriPipelineToolbar />
            {showSuggested && suggestedDeals.length > 0 && (
              <div className="shrink-0 border-b border-border bg-muted/20 px-5 py-3">
                <div className="mb-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Suggested from Inbox
                </div>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {suggestedDeals.map((deal) => (
                    <AriSuggestedDealCard key={deal.id} deal={deal} onAdd={addSuggestedDeal} />
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <header className="border-b border-border bg-card/50">
            <div className="px-6 py-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="flex size-10 items-center justify-center rounded-md bg-muted">
                    <Folder className="size-5 text-muted-foreground" aria-hidden="true" />
                  </div>
                  <div>
                    <h1 className="font-semibold tracking-tight text-foreground">Deal Pipeline</h1>
                    <p className="text-sm text-muted-foreground">Manage and track deals across stages</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <TabsList indicatorColor="secondary" className="text-sm">
                    <TabsTab value="pipeline">
                      <LayoutGrid className="size-3.5" />
                      <span className="hidden sm:inline">Pipeline</span>
                    </TabsTab>
                    <TabsTab value="table">
                      <Table2 className="size-3.5" />
                      <span className="hidden sm:inline">Table</span>
                    </TabsTab>
                  </TabsList>

                  <Button
                    onClick={() => navigate(isDemo ? PATH_ROOT.demo.newDeal : PATH_ROOT.main.newDeal)}
                    className="gap-2"
                  >
                    <Plus className="size-4" />
                    <span className="hidden sm:inline">New Deal</span>
                  </Button>
                </div>
              </div>
            </div>

            <div className="px-6 pb-4">
              <div className="flex gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <div className="size-2 rounded-full bg-orange-500" aria-label="In Progress status" />
                  <span className="text-muted-foreground">In Progress</span>
                  <span className="font-medium text-foreground">{stats.inProgress}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="size-2 rounded-full bg-orange-500" aria-label="Signing status" />
                  <span className="text-muted-foreground">Signing</span>
                  <span className="font-medium text-foreground">{stats.signing}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="size-2 rounded-full bg-orange-500" aria-label="Complete status" />
                  <span className="text-muted-foreground">Complete</span>
                  <span className="font-medium text-foreground">{stats.complete}</span>
                </div>
                <div className="flex items-center gap-2">
                  <DollarSign className="size-4 text-muted-foreground/50" aria-hidden="true" />
                  <span className="text-muted-foreground">Total Value</span>
                  <span className="font-medium text-foreground">{formatCurrency(stats.totalValue)}</span>
                </div>
              </div>
            </div>
          </header>
        )}

        {viewMode === "pipeline" && !isAriPipeline && (
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-4 border-b border-border px-6 py-3">
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <div className="relative max-w-sm flex-1">
                <label htmlFor="deal-search" className="sr-only">
                  Search deals
                </label>
                <Input
                  size="lg"
                  id="deal-search"
                  startIcon={<Search className="size-2 text-muted-foreground pointer-events-none" />}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search deals…"
                  aria-label="Search deals"
                />
              </div>
            </div>

            <PipelinePresets activePreset={activePreset} onPresetChange={setActivePreset} />
          </div>
        )}
      </div>

      <TabsPanel value="pipeline" className="min-h-0 flex-1">
        <DealPipelineBoard
          deals={filteredDeals}
          isAriPipeline={isAriPipeline}
          onRequestDelete={(deal) => setPendingDeleteDeal(deal)}
          onDealsChange={(updatedDeals) => {
            const updatedStageByDealId = new Map(updatedDeals.map((deal) => [deal.id, deal.stage]))

            setDeals((currentDeals) => {
              let hasChanges = false
              const nextDeals = currentDeals.map((deal) => {
                const nextStage = updatedStageByDealId.get(deal.id)
                if (!nextStage || nextStage === deal.stage) {
                  return deal
                }

                hasChanges = true
                return { ...deal, stage: nextStage }
              })

              return hasChanges ? nextDeals : currentDeals
            })
          }}
          visibleColumns={visibleColumns as PipelineStage[]}
        />
      </TabsPanel>

      {!isAriPipeline && (
        <TabsPanel value="table" className="min-h-0 flex-1">
          <DealsTableView
            onDealClick={(dealId) =>
              navigate(`${isDemo ? PATH_ROOT.demo.deal : PATH_ROOT.main.deal}/${dealId}`)
            }
          />
        </TabsPanel>
      )}

      {/* Page-level delete confirmation. Destructive action ONLY fires on
          explicit Delete click — closing the dialog (Escape / backdrop /
          Cancel) never deletes. */}
      <AlertDialog
        open={Boolean(pendingDeleteDeal)}
        onOpenChange={(open) => {
          if (!open && !deletingDeal) setPendingDeleteDeal(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this deal?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be easily undone. The deal will be removed from your
              pipeline. Any linked documents and threads stay in your inbox.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="outline"
              disabled={deletingDeal}
              onClick={() => setPendingDeleteDeal(null)}
            >
              Cancel
            </Button>
            <Button variant="destructive" disabled={deletingDeal} onClick={confirmDeleteDeal}>
              {deletingDeal ? "Deleting…" : "Delete"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Tabs>
  )
}

export default DealPipelinePage
